import { observer } from "mobx-react-lite";
import React from "react";
import { useApp } from "../../../context";
import { useNavigate } from "react-router";
import WaveSurfer from "wavesurfer.js";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  makeStyles,
  Typography,
} from "@material-ui/core";

const BASE_SCORE = 100;
const MAX_MULTIPLIER = 3;
const DECAY_SECONDS = 45;
const MAX_LIVES = 3;
const BATCH_SIZE = 5;
const REFETCH_THRESHOLD = 2; // fetch next batch when this many questions remain

function calcMultiplier(elapsed: number) {
  return Math.max(1, MAX_MULTIPLIER - (elapsed / DECAY_SECONDS) * (MAX_MULTIPLIER - 1));
}

function calcScore(elapsed: number, streak: number) {
  const base = Math.round(BASE_SCORE * calcMultiplier(elapsed));
  const bonus = streak >= 5 ? 1.5 : streak >= 3 ? 1.25 : 1;
  return Math.round(base * bonus);
}

interface SurvivalQuestion {
  objectId: string;
  name: string;
  synthetic: boolean;
  audio?: { url: string; name: string };
}

const useStyles = makeStyles((theme) => ({
  questionContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(2, 0),
    maxWidth: 520,
    margin: "0 auto",
  },
  questionAudio: {
    width: "100%",
    marginBottom: 12,
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  audioControls: {
    display: "flex",
    gap: theme.spacing(1),
    margin: "8px auto 10px",
    width: "fit-content",
  },
  choiceButtons: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  choiceButton: {
    minWidth: "150px",
    padding: theme.spacing(1, 3),
  },
}));

export const SurvivalRoute = observer(function SurvivalRoute() {
  const app = useApp();
  const navigate = useNavigate();
  const classes = useStyles();

  const [queue, setQueue] = React.useState<SurvivalQuestion[]>([]);
  const [seenIds, setSeenIds] = React.useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [lives, setLives] = React.useState(MAX_LIVES);
  const [streak, setStreak] = React.useState(0);
  const [totalScore, setTotalScore] = React.useState(0);
  const [answeredTotal, setAnsweredTotal] = React.useState(0);
  const [correctTotal, setCorrectTotal] = React.useState(0);
  const [gameOver, setGameOver] = React.useState(false);

  const [currentAnswer, setCurrentAnswer] = React.useState<1 | 2 | null>(null);
  const [currentFeedback, setCurrentFeedback] = React.useState<"correct" | "wrong" | null>(null);
  const [elapsed, setElapsed] = React.useState(0);

  const [fetchingMore, setFetchingMore] = React.useState(false);
  const [loadingCurrent, setLoadingCurrent] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const wavesurferRef = React.useRef<WaveSurfer | null>(null);
  const waveContainerRef = React.useRef<HTMLDivElement | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = React.useRef(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [audioReady, setAudioReady] = React.useState(false);
  const fetching = React.useRef(false);

  // Fetch a batch of questions, excluding already-seen IDs
  const fetchBatch = React.useCallback(async (exclude: string[]) => {
    if (fetching.current) return;
    fetching.current = true;
    setFetchingMore(true);
    try {
      const { result } = await app.parse.cc("game-fetch-questions", {
        count: BATCH_SIZE,
        excludeIds: exclude,
      });
      setQueue((prev) => [...prev, ...result]);
      setSeenIds((prev) => [...prev, ...result.map((q: SurvivalQuestion) => q.objectId)]);
    } catch {
      // silently ignore — user will see loading state
    } finally {
      setFetchingMore(false);
      fetching.current = false;
    }
  }, [app.parse]);

  // Initial fetch
  React.useEffect(() => {
    fetchBatch([]);
  }, []);

  const currentQuestion = queue[currentIndex] ?? null;

  // Pre-fetch next batch when running low
  React.useEffect(() => {
    const remaining = queue.length - currentIndex;
    if (remaining <= REFETCH_THRESHOLD && !fetchingMore && !fetching.current && !gameOver) {
      fetchBatch(seenIds);
    }
  }, [currentIndex, queue.length, gameOver]);

  // Load WaveSurfer for current question
  React.useEffect(() => {
    if (!currentQuestion?.audio?.url || gameOver) return;

    // Destroy previous instance
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }

    setAudioReady(false);
    setIsPlaying(false);
    setLoadingCurrent(true);

    const container = waveContainerRef.current;
    if (!container) return;

    const ws = WaveSurfer.create({
      container,
      waveColor: "#4fc3f7",
      progressColor: "#29b6f6",
      cursorColor: "#1976d2",
      barWidth: 2,
      height: 120,
    });

    ws.load(currentQuestion.audio.url);
    wavesurferRef.current = ws;

    ws.on("ready", () => {
      setAudioReady(true);
      setLoadingCurrent(false);
    });
    ws.on("play", () => setIsPlaying(true));
    ws.on("pause", () => setIsPlaying(false));
    ws.on("finish", () => setIsPlaying(false));

    return () => {
      ws.destroy();
      wavesurferRef.current = null;
    };
  }, [currentQuestion?.objectId, gameOver]);

  // Timer per question
  React.useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    elapsedRef.current = 0;
    setElapsed(0);

    if (!gameOver && currentQuestion) {
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
    }

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [currentIndex, gameOver]);

  const handleAnswer = (answer: 1 | 2) => {
    if (currentAnswer !== null || !currentQuestion) return;
    if (timerRef.current) clearInterval(timerRef.current);

    const isCorrect = answer === (currentQuestion.synthetic ? 2 : 1);
    const newStreak = isCorrect ? streak + 1 : 0;
    const scored = isCorrect ? calcScore(elapsedRef.current, newStreak) : 0;
    const newLives = isCorrect ? lives : lives - 1;

    setCurrentAnswer(answer);
    setCurrentFeedback(isCorrect ? "correct" : "wrong");
    setStreak(newStreak);
    setTotalScore((s) => s + scored);
    setAnsweredTotal((n) => n + 1);
    if (isCorrect) setCorrectTotal((n) => n + 1);
    setLives(newLives);

    wavesurferRef.current?.pause();

    if (newLives <= 0) {
      setTimeout(() => setGameOver(true), 1000);
    } else {
      setTimeout(() => {
        setCurrentAnswer(null);
        setCurrentFeedback(null);
        setCurrentIndex((i) => i + 1);
      }, 800);
    }
  };

  const handleShare = () => {
    const emoji = totalScore >= 1000 ? "🏆" : totalScore >= 500 ? "🥇" : totalScore >= 200 ? "👍" : "📚";
    const text = `${emoji} Ich habe ${answeredTotal} Fragen im Survival-Modus überlebt! (${correctTotal} richtig, ${totalScore} Pts)\n→ ${window.location.origin}/benchmark`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (gameOver) {
    return (
      <Card style={{ padding: 20 }}>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 24, padding: "32px 16px", maxWidth: 480, margin: "0 auto" }}>
          <Typography variant="h2" style={{ fontSize: 64, lineHeight: 1 }}>
            {totalScore >= 1000 ? "🏆" : totalScore >= 500 ? "🥇" : totalScore >= 200 ? "👍" : "📚"}
          </Typography>
          <Typography variant="h5" align="center" style={{ fontWeight: 700 }}>
            Survival beendet!
          </Typography>
          <Box style={{ background: "rgba(0,0,0,0.06)", borderRadius: 16, padding: "24px 40px", textAlign: "center", width: "100%" }}>
            <Typography variant="h3" style={{ fontWeight: 800 }}>{totalScore}</Typography>
            <Typography variant="subtitle1" color="textSecondary">Punkte</Typography>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>{correctTotal} / {answeredTotal}</Typography>
            <Typography variant="body2" color="textSecondary">richtig erkannt</Typography>
          </Box>

          <Button variant="outlined" onClick={handleShare} style={{ minWidth: 180 }}>
            {copied ? "✓ Kopiert!" : "Ergebnis teilen"}
          </Button>

          <Box display="flex" style={{ gap: 12 }}>
            <Button variant="contained" color="primary" onClick={() => navigate("/benchmark/survival")}>
              Nochmal
            </Button>
            <Button variant="outlined" onClick={() => navigate("/benchmark")}>
              Startseite
            </Button>
          </Box>
        </Box>
      </Card>
    );
  }

  const isLoading = !currentQuestion || loadingCurrent;

  return (
    <Card style={{ padding: 20 }}>
      {/* Header: lives + streak + score */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" style={{ gap: 4 }}>
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <Typography key={i} style={{ fontSize: 22, opacity: i < lives ? 1 : 0.2 }}>❤️</Typography>
          ))}
        </Box>
        <Box display="flex" style={{ gap: 12, alignItems: "center" }}>
          {streak >= 3 && (
            <Typography variant="body2" style={{ color: "#ff9800", fontWeight: 700 }}>
              🔥 {streak}×
            </Typography>
          )}
          <Typography variant="h6" style={{ fontWeight: 700 }}>{totalScore} Pts</Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" mb={1}>
        <Typography variant="caption" color="textSecondary">
          Frage {answeredTotal + 1} · {correctTotal} richtig
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: "60px 0", gap: 16 }}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">Wird geladen...</Typography>
        </Box>
      ) : (
        <div className={classes.questionContent}>
          {/* Timer + multiplier */}
          <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
            <Typography variant="h6" style={{ fontFamily: "monospace" }}>
              ⏱ {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
            </Typography>
            {!currentAnswer && (
              <Typography variant="body2" color="textSecondary" style={{ alignSelf: "center" }}>
                {calcMultiplier(elapsed).toFixed(1)}× Mult.
              </Typography>
            )}
          </Box>

          {/* Feedback badge */}
          {currentFeedback && (
            <Box mb={2} style={{
              padding: "8px 24px",
              borderRadius: 8,
              background: currentFeedback === "correct" ? "#4caf50" : "#f44336",
              color: "#fff",
              fontWeight: 700,
              fontSize: 18,
            }}>
              {currentFeedback === "correct"
                ? `✓ +${calcScore(elapsedRef.current, streak)} Punkte`
                : `✗ Falsch — noch ${lives} ${lives === 1 ? "Leben" : "Leben"}`}
            </Box>
          )}

          {/* Waveform */}
          {currentQuestion.audio && (
            <div className={classes.questionAudio}>
              <div ref={waveContainerRef} style={{ width: "100%", height: "120px" }} />
              <div className={classes.audioControls}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!audioReady}
                  onClick={() => {
                    const ws = wavesurferRef.current;
                    if (ws) isPlaying ? ws.pause() : ws.play();
                  }}
                >
                  {isPlaying ? "Pause" : "Play"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!audioReady}
                  onClick={() => {
                    const ws = wavesurferRef.current;
                    if (ws) { ws.seekTo(0); }
                  }}
                >
                  Restart
                </Button>
              </div>
            </div>
          )}

          <Typography variant="subtitle2" align="center" gutterBottom>
            Ist diese Stimme echt oder KI-generiert?
          </Typography>
          <div className={classes.choiceButtons}>
            <Button
              className={classes.choiceButton}
              variant={currentAnswer === 1 ? "contained" : "outlined"}
              color={currentAnswer === 1 ? "primary" : "default"}
              disabled={currentAnswer !== null}
              onClick={() => handleAnswer(1)}
            >
              Echt
            </Button>
            <Button
              className={classes.choiceButton}
              variant={currentAnswer === 2 ? "contained" : "outlined"}
              color={currentAnswer === 2 ? "primary" : "default"}
              disabled={currentAnswer !== null}
              onClick={() => handleAnswer(2)}
            >
              KI-generiert
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
});
